import React, { useState, useEffect } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';

const TaxiDispatcher = () => {
  const [activeRequests, setActiveRequests] = useState([]);
  const [callLogs, setCallLogs] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [testText, setTestText] = useState('');
  const [bertResult, setBertResult] = useState(null);

  // Load data from backend
  useEffect(() => {
    loadRequests();
    loadDrivers();
    
    // Reload data every 30 seconds
    const interval = setInterval(() => {
      loadRequests();
      loadDrivers();
    }, 30000);
    
    return () => clearInterval(interval);
  }, []);

  const loadRequests = async () => {
    try {
      const response = await fetch('/api/requests');
      if (response.ok) {
        const data = await response.json();
        setActiveRequests(data);
      }
    } catch (error) {
      console.error('Error loading requests:', error);
      // Fallback to mock data for demo
      setActiveRequests([
        {
          id: 'REQ001',
          call_sid: 'CA123456789',
          phone_number: '+421901234567',
          pickup_location: 'Bratislava centrum',
          timestamp: new Date().toISOString(),
          status: 'pending',
          audio_transcription: 'potrebujem taxík do centra'
        },
        {
          id: 'REQ002', 
          call_sid: 'CA987654321',
          phone_number: '+421905555666',
          pickup_location: 'Košice hlavná stanica',
          timestamp: new Date(Date.now() - 300000).toISOString(),
          status: 'assigned',
          driver_name: 'Peter Novák',
          audio_transcription: 'na železničnú stanicu v košiciach'
        }
      ]);
    }
  };

  const loadDrivers = async () => {
    try {
      const response = await fetch('/api/drivers');
      if (response.ok) {
        const data = await response.json();
        setDrivers(data);
      }
    } catch (error) {
      console.error('Error loading drivers:', error);
      // Fallback to mock data for demo
      setDrivers([
        { id: 'DR001', name: 'Peter Novák', status: 'busy', location: 'Košice centrum' },
        { id: 'DR002', name: 'Jana Krásna', status: 'available', location: 'Bratislava Petržalka' },
        { id: 'DR003', name: 'Michal Svoboda', status: 'available', location: 'Bratislava centrum' }
      ]);
    }
  };

  const assignDriver = async (requestId, driverId) => {
    setLoading(true);
    try {
      const response = await fetch('/api/assign-driver', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ request_id: requestId, driver_id: driverId })
      });
      
      if (response.ok) {
        const result = await response.json();
        // Update local state
        setActiveRequests(prev => 
          prev.map(req => 
            req.id === requestId 
              ? { ...req, status: 'assigned', driver_id: driverId, driver_name: result.driver_name }
              : req
          )
        );
        
        setDrivers(prev =>
          prev.map(driver =>
            driver.id === driverId
              ? { ...driver, status: 'busy' }
              : driver
          )
        );
      }
    } catch (error) {
      console.error('Error assigning driver:', error);
    } finally {
      setLoading(false);
    }
  };

  const testSlovakBert = async () => {
    if (!testText.trim()) return;
    
    setLoading(true);
    try {
      const response = await fetch('/api/test-slovak-bert', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text: testText })
      });
      
      if (response.ok) {
        const result = await response.json();
        setBertResult(result);
      }
    } catch (error) {
      console.error('Error testing Slovak BERT:', error);
      // Mock result for demo
      setBertResult({
        input_text: testText,
        extracted_location: testText.includes('centrum') ? 'Bratislava centrum' : 
                          testText.includes('stanica') ? 'Železničná stanica' : 'Nerozpoznané',
        timestamp: new Date().toISOString()
      });
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return '#F59E0B';
      case 'assigned': return '#10B981';
      case 'completed': return '#6B7280';
      case 'cancelled': return '#EF4444';
      default: return '#9CA3AF';
    }
  };

  const formatTime = (timestamp) => {
    return new Date(timestamp).toLocaleTimeString('sk-SK', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const pendingCount = activeRequests.filter(r => r.status === 'pending').length;
  const availableDrivers = drivers.filter(d => d.status === 'available').length;

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #0a0a0a 0%, #1a1a2e 50%, #16213e 100%)',
      color: 'white'
    }}>
      <Header />
      
      <div style={{ padding: '120px 20px 60px', maxWidth: '1400px', margin: '0 auto' }}>
        <h1 style={{
          fontSize: '3rem',
          textAlign: 'center',
          marginBottom: '20px',
          background: 'linear-gradient(135deg, #F28C38 0%, #ff6b35 100%)',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent'
        }}>
          🚖 AI Taxi Dispečing
        </h1>

        <p style={{ 
          textAlign: 'center', 
          opacity: '0.8', 
          marginBottom: '40px', 
          fontSize: '1.2rem' 
        }}>
          Inteligentný systém správy taxi objednávok s podporou slovenčiny
        </p>

        {/* Real-time Stats */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
          gap: '20px',
          marginBottom: '40px'
        }}>
          <div style={{
            background: 'rgba(255,255,255,0.05)',
            padding: '20px',
            borderRadius: '15px',
            border: '1px solid rgba(242, 140, 56, 0.3)',
            textAlign: 'center'
          }}>
            <h3 style={{ color: '#F28C38', marginBottom: '10px' }}>📞 Aktívne volania</h3>
            <div style={{ fontSize: '2rem', fontWeight: 'bold' }}>
              {pendingCount}
            </div>
          </div>

          <div style={{
            background: 'rgba(255,255,255,0.05)',
            padding: '20px',
            borderRadius: '15px',
            border: '1px solid rgba(16, 185, 129, 0.3)',
            textAlign: 'center'
          }}>
            <h3 style={{ color: '#10B981', marginBottom: '10px' }}>🚗 Dostupní vodiči</h3>
            <div style={{ fontSize: '2rem', fontWeight: 'bold' }}>
              {availableDrivers}
            </div>
          </div>

          <div style={{
            background: 'rgba(255,255,255,0.05)',
            padding: '20px',
            borderRadius: '15px',
            border: '1px solid rgba(59, 130, 246, 0.3)',
            textAlign: 'center'
          }}>
            <h3 style={{ color: '#3B82F6', marginBottom: '10px' }}>✅ Dnes dokončené</h3>
            <div style={{ fontSize: '2rem', fontWeight: 'bold' }}>47</div>
          </div>

          <div style={{
            background: 'rgba(255,255,255,0.05)',
            padding: '20px',
            borderRadius: '15px',
            border: '1px solid rgba(168, 85, 247, 0.3)',
            textAlign: 'center'
          }}>
            <h3 style={{ color: '#A855F7', marginBottom: '10px' }}>🎯 Slovak BERT</h3>
            <div style={{ fontSize: '1.2rem', fontWeight: 'bold', color: '#10B981' }}>
              ONLINE
            </div>
          </div>
        </div>

        {/* Slovak BERT Test Section */}
        <div style={{
          background: 'rgba(255,255,255,0.05)',
          borderRadius: '20px',
          padding: '30px',
          border: '1px solid rgba(168, 85, 247, 0.3)',
          marginBottom: '30px'
        }}>
          <h2 style={{ color: '#A855F7', marginBottom: '20px' }}>
            🧠 Slovak BERT Test
          </h2>
          
          <div style={{ display: 'flex', gap: '15px', marginBottom: '20px' }}>
            <input
              type="text"
              placeholder="Napíšte text pre extrakciu lokácie (napr. 'potrebujem taxík na hlavné námestie v bratislave')"
              value={testText}
              onChange={(e) => setTestText(e.target.value)}
              style={{
                flex: 1,
                padding: '12px 20px',
                borderRadius: '10px',
                border: '1px solid #A855F7',
                background: 'rgba(255,255,255,0.1)',
                color: 'white',
                fontSize: '1rem'
              }}
              onKeyPress={(e) => e.key === 'Enter' && testSlovakBert()}
            />
            <button
              onClick={testSlovakBert}
              disabled={loading || !testText.trim()}
              style={{
                padding: '12px 25px',
                background: loading ? '#666' : 'linear-gradient(135deg, #A855F7 0%, #7C3AED 100%)',
                color: 'white',
                border: 'none',
                borderRadius: '10px',
                cursor: loading ? 'not-allowed' : 'pointer',
                minWidth: '120px'
              }}
            >
              {loading ? '⏳' : '🧠 Test'}
            </button>
          </div>

          {bertResult && (
            <div style={{
              background: 'rgba(0,0,0,0.3)',
              padding: '20px',
              borderRadius: '12px',
              border: '1px solid rgba(34, 197, 94, 0.3)'
            }}>
              <h4 style={{ color: '#22C55E', marginBottom: '15px' }}>✅ Výsledok:</h4>
              <div style={{ marginBottom: '10px' }}>
                <strong>Vstup:</strong> "{bertResult.input_text}"
              </div>
              <div style={{ marginBottom: '10px' }}>
                <strong>Extrahovaná lokácia:</strong> <span style={{ color: '#F28C38' }}>
                  {bertResult.extracted_location || 'Nerozpoznané'}
                </span>
              </div>
              <div style={{ fontSize: '0.9rem', opacity: '0.7' }}>
                Čas: {new Date(bertResult.timestamp).toLocaleTimeString('sk-SK')}
              </div>
            </div>
          )}
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '30px' }}>
          {/* Active Requests */}
          <div style={{
            background: 'rgba(255,255,255,0.05)',
            borderRadius: '20px',
            padding: '30px',
            border: '1px solid rgba(242, 140, 56, 0.3)'
          }}>
            <h2 style={{ color: '#F28C38', marginBottom: '20px' }}>
              📋 Aktívne požiadavky
            </h2>

            {activeRequests.length === 0 ? (
              <div style={{
                textAlign: 'center',
                padding: '40px',
                opacity: '0.7'
              }}>
                📱 Žiadne aktívne požiadavky
              </div>
            ) : (
              activeRequests.map(request => (
                <div
                  key={request.id}
                  style={{
                    background: 'rgba(255,255,255,0.03)',
                    padding: '20px',
                    borderRadius: '15px',
                    marginBottom: '15px',
                    border: `1px solid ${getStatusColor(request.status)}40`,
                    cursor: 'pointer'
                  }}
                  onClick={() => setSelectedRequest(request)}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                      <div style={{ fontWeight: 'bold', marginBottom: '5px' }}>
                        📍 {request.pickup_location}
                      </div>
                      <div style={{ opacity: '0.7', fontSize: '0.9rem' }}>
                        📞 {request.phone_number} • ⏰ {formatTime(request.timestamp)}
                      </div>
                      {request.driver_name && (
                        <div style={{ marginTop: '5px', color: '#10B981', fontSize: '0.9rem' }}>
                          🚗 {request.driver_name}
                        </div>
                      )}
                    </div>
                    <div style={{
                      padding: '5px 15px',
                      borderRadius: '20px',
                      background: getStatusColor(request.status),
                      color: 'white',
                      fontSize: '0.8rem',
                      fontWeight: 'bold'
                    }}>
                      {request.status === 'pending' ? 'ČAKÁ' : 
                       request.status === 'assigned' ? 'PRIRADENÉ' : 
                       request.status.toUpperCase()}
                    </div>
                  </div>

                  {request.audio_transcription && (
                    <div style={{
                      marginTop: '10px',
                      padding: '10px',
                      background: 'rgba(0,0,0,0.3)',
                      borderRadius: '8px',
                      fontSize: '0.9rem',
                      fontStyle: 'italic'
                    }}>
                      💬 "{request.audio_transcription}"
                    </div>
                  )}

                  {request.status === 'pending' && (
                    <div style={{ marginTop: '15px', display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                      {drivers.filter(d => d.status === 'available').map(driver => (
                        <button
                          key={driver.id}
                          onClick={(e) => {
                            e.stopPropagation();
                            assignDriver(request.id, driver.id);
                          }}
                          disabled={loading}
                          style={{
                            padding: '8px 15px',
                            background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
                            color: 'white',
                            border: 'none',
                            borderRadius: '8px',
                            fontSize: '0.8rem',
                            cursor: 'pointer',
                            opacity: loading ? 0.5 : 1
                          }}
                        >
                          🚗 {driver.name}
                        </button>
                      ))}
                      {drivers.filter(d => d.status === 'available').length === 0 && (
                        <div style={{ color: '#EF4444', fontSize: '0.9rem' }}>
                          ⚠️ Žiadni dostupní vodiči
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))
            )}
          </div>

          {/* Driver Status */}
          <div style={{
            background: 'rgba(255,255,255,0.05)',
            borderRadius: '20px',
            padding: '30px',
            border: '1px solid rgba(16, 185, 129, 0.3)'
          }}>
            <h2 style={{ color: '#10B981', marginBottom: '20px' }}>
              👥 Vodiči
            </h2>

            {drivers.map(driver => (
              <div
                key={driver.id}
                style={{
                  background: 'rgba(255,255,255,0.03)',
                  padding: '15px',
                  borderRadius: '12px',
                  marginBottom: '12px',
                  border: `1px solid ${driver.status === 'available' ? '#10B981' : '#6B7280'}40`
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <div style={{ fontWeight: 'bold' }}>{driver.name}</div>
                    <div style={{ opacity: '0.7', fontSize: '0.8rem' }}>
                      📍 {driver.location}
                    </div>
                    <div style={{ 
                      fontSize: '0.8rem', 
                      color: driver.status === 'available' ? '#10B981' : '#F59E0B',
                      marginTop: '3px'
                    }}>
                      {driver.status === 'available' ? '✅ Dostupný' : '🚗 Obsadený'}
                    </div>
                  </div>
                  <div style={{
                    width: '12px',
                    height: '12px',
                    borderRadius: '50%',
                    background: driver.status === 'available' ? '#10B981' : '#6B7280'
                  }}></div>
                </div>
              </div>
            ))}

            <button style={{
              width: '100%',
              padding: '12px',
              background: 'linear-gradient(135deg, #F28C38 0%, #ff6b35 100%)',
              color: 'white',
              border: 'none',
              borderRadius: '10px',
              marginTop: '15px',
              cursor: 'pointer'
            }}>
              ➕ Pridať vodiča
            </button>
          </div>
        </div>

        {/* Request Detail Modal */}
        {selectedRequest && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.8)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }} onClick={() => setSelectedRequest(null)}>
            <div style={{
              background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)',
              padding: '30px',
              borderRadius: '20px',
              border: '1px solid rgba(242, 140, 56, 0.3)',
              maxWidth: '500px',
              width: '90%',
              maxHeight: '80vh',
              overflow: 'auto'
            }} onClick={(e) => e.stopPropagation()}>
              <h3 style={{ color: '#F28C38', marginBottom: '20px' }}>
                📋 Detail požiadavky
              </h3>
              
              <div style={{ marginBottom: '15px' }}>
                <strong>ID:</strong> {selectedRequest.id}
              </div>
              <div style={{ marginBottom: '15px' }}>
                <strong>Telefón:</strong> {selectedRequest.phone_number}
              </div>
              <div style={{ marginBottom: '15px' }}>
                <strong>Lokácia:</strong> {selectedRequest.pickup_location}
              </div>
              <div style={{ marginBottom: '15px' }}>
                <strong>Čas:</strong> {new Date(selectedRequest.timestamp).toLocaleString('sk-SK')}
              </div>
              <div style={{ marginBottom: '15px' }}>
                <strong>Status:</strong> <span style={{ color: getStatusColor(selectedRequest.status) }}>
                  {selectedRequest.status === 'pending' ? 'Čaká na priradenie' : 
                   selectedRequest.status === 'assigned' ? 'Priradené' : 
                   selectedRequest.status}
                </span>
              </div>
              
              {selectedRequest.driver_name && (
                <div style={{ marginBottom: '15px' }}>
                  <strong>Vodič:</strong> {selectedRequest.driver_name}
                </div>
              )}
              
              {selectedRequest.audio_transcription && (
                <div style={{ marginBottom: '15px' }}>
                  <strong>Prepis hovoru:</strong>
                  <div style={{
                    marginTop: '5px',
                    padding: '10px',
                    background: 'rgba(0,0,0,0.3)',
                    borderRadius: '8px',
                    fontStyle: 'italic'
                  }}>
                    "{selectedRequest.audio_transcription}"
                  </div>
                </div>
              )}

              <button
                onClick={() => setSelectedRequest(null)}
                style={{
                  width: '100%',
                  padding: '12px',
                  background: '#6B7280',
                  color: 'white',
                  border: 'none',
                  borderRadius: '10px',
                  cursor: 'pointer'
                }}
              >
                Zavrieť
              </button>
            </div>
          </div>
        )}
      </div>

      <Footer />
    </div>
  );
};

export default TaxiDispatcher; 